import { useState } from 'react';
import { useAuth } from '../store/auth';
import { useWorkspace } from '../store/workspace';
import { formatPhone } from '../lib/phone';
import { downloadSlips, signInAddress, type Slip } from '../lib/slips';

/**
 * The centre a slip is printed for, and where its candidates sign in.
 *
 * A teacher is inside their own centre and has its address. A platform
 * administrator working inside a centre has its name but not its address, so
 * the slip points at the general sign-in page — which signs anyone in.
 */
export function useSlipCentre(): { centre: string; address: string } {
  const organization = useAuth((s) => s.organization);
  const scopedName = useWorkspace((w) => w.organizationName);
  const scopedId = useWorkspace((w) => w.organizationId);
  if (scopedId && scopedId !== organization?.id) {
    return { centre: scopedName ?? 'Your centre', address: signInAddress(null) };
  }
  return {
    centre: organization?.name ?? scopedName ?? 'Your centre',
    address: signInAddress(organization?.isPlatform ? null : organization?.slug),
  };
}

/** A student as the server hands them back with a password, made into a slip. */
export const slipFor = (student: {
  fullName: string;
  candidateNumber?: string | null;
  phone?: string | null;
  password: string;
}): Slip => ({
  name: student.fullName,
  candidateNumber: student.candidateNumber ?? null,
  signIn: student.phone ? formatPhone(student.phone) : (student.candidateNumber ?? ''),
  password: student.password,
});

/** Saves a PDF of sign-in slips, ten to an A4 sheet, ready to print and cut. */
export function SlipButton({
  slips,
  filename,
  label,
  primary = false,
}: {
  slips: Slip[];
  filename: string;
  label: string;
  primary?: boolean;
}) {
  const { centre, address } = useSlipCentre();
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  async function save() {
    setBusy(true);
    setFailed(false);
    try {
      await downloadSlips({ centre, address, slips }, filename);
    } catch {
      setFailed(true);
    } finally {
      setBusy(false);
    }
  }

  return (
    <button
      type="button"
      className={`btn${primary ? ' btn--primary' : ''}`}
      disabled={busy || slips.length === 0}
      onClick={() => void save()}
    >
      {busy ? 'Preparing…' : failed ? 'Could not make the file — try again' : label}
    </button>
  );
}
